

import React, { useEffect, useState } from "react";
import Table from "../../components/Table";
import axios from "axios";
import AddCandidateModal from "./AddCandidate";
import { toast } from "react-toastify";
import { useRole } from "../../context/RoleContext";
import EmailModal from "../../components/EmailModal ";

const Candidates = () => {
  const [candidateData, setCandidateData] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]); // Stores IDs of selected rows
  const [selectedCandidateForEmail, setSelectedCandidateForEmail] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [candidateToEdit, setCandidateToEdit] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { role } = useRole();
  const isSuperAdmin = role === "superadmin";

  useEffect(() => {
    getCandidates();
  }, [apiUrl]);

  const getCandidates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/hr/get-candidate`, {
        withCredentials: true,
      });
      setCandidateData(response.data.candidates);
      setSelectedCandidateIds([]); // Clear selection when data is re-fetched
    } catch (error) {
      toast.error("Failed to fetch candidates");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles the selection state of a candidate when a row is clicked.
   * @param {object} candidate - The candidate object from the clicked row.
   */
  const handleRowClick = (candidate) => {
    const selectedId = candidate._id;
    
    // Check if the ID is already in the selection array
    if (selectedCandidateIds.includes(selectedId)) {
      // If it is, remove it (deselect)
      setSelectedCandidateIds(prevSelected => prevSelected.filter(id => id !== selectedId));
    } else {
      // If it's not, add it (select)
      setSelectedCandidateIds(prevSelected => [...prevSelected, selectedId]);
    }
  };

  const handleEntriesChange = (value) => {
    setEntries(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (value) => {
    setSearchQuery(value?.toLowerCase());
    setCurrentPage(1);
  };

  const filterCandidates = candidateData.filter((candidate) =>
    candidate.name?.toLowerCase().includes(searchQuery)
  );

  const CandidatesPaginateData = filterCandidates.slice((currentPage - 1) * entries, (currentPage - 1) * entries + entries);

  /**
   * Opens the edit modal with the data of the single selected candidate.
   */
  const handleEditSelected = () => {
    const candidateId = selectedCandidateIds[0];
    const candidateToUpdate = candidateData.find(c => c._id === candidateId);
    setCandidateToEdit(candidateToUpdate);
    setIsModalOpen(true);
  };

  /**
   * Shows a confirmation dialog and then proceeds to delete selected candidates.
   */
  const handleDeleteSelected = () => {
    // This is the confirmation popup you requested
    if (window.confirm(`Are you sure you want to delete these ${selectedCandidateIds.length} selected candidates?`)) {
      handleDelete(selectedCandidateIds);
    }
  };

  /**
   * Sends a request to the API to delete candidates.
   * @param {string[]} ids - An array of candidate IDs to delete.
   */
  const handleDelete = async (ids) => {
    try {
      setLoading(true);
      await axios.delete(`${apiUrl}/api/hr/delete-candidate`, {
        data: { candidateId: ids },
        withCredentials: true,
      });
      toast.success("Candidate(s) deleted successfully");
      getCandidates(); // Refreshes data and clears selection
    } catch (error) {
      console.error("Error deleting candidates:", error);
      toast.error("Failed to delete candidates");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMail = (candidateData) => {
    setSelectedCandidateForEmail(candidateData);
    setEmailModalOpen(true);
  };

  const handleEmailSubmit = async (subject, message) => {
    try {
        setLoading(true);
        const response = await axios.post(`${apiUrl}/api/sendmail`, {
          to: selectedCandidateForEmail.email,
          subject: subject,
          text: message,
        });
        toast.success(response.data.message);
        setEmailModalOpen(false);
      } catch (error) {
        toast.error(error.response?.data?.message || "Error sending email");
      } finally {
        setLoading(false);
      }
  };

  // Columns for the table, without any checkboxes or action columns.
  const columns = [
    ...(isSuperAdmin ? [{ header: "Organization", accessor: "org_id" }] : []),
    { header: "Name", accessor: "name" },
    { header: "Phone", accessor: "phone" },
    { header: "Email", accessor: "email" },
    { header: "Role Applied", accessor: "role" },
    { header: "Address", accessor: "address" },
    {
      header: "Resume",
      accessor: "resume",
      render: (row) => (
        <button className="text-xl" onClick={(e) => { e.stopPropagation(); window.open(row.resume, "_blank"); }}>
          📙
        </button>
      ),
    },
    {
      header: "Mail",
      accessor: "mail",
      render: (row) => (
        <button className="text-xl" onClick={(e) => { e.stopPropagation(); handleSendMail(row); }}>
          📧
        </button>
      ),
    },
  ];

  
  const generateActions = () => {
    const actions = [
      { label: "🔄 Reload", onClick: getCandidates },
      { label: "➕ Add Candidate", onClick: () => { setCandidateToEdit(null); setIsModalOpen(true); } },
    ];

    const selectedCount = selectedCandidateIds.length;

    // Show Edit button ONLY when exactly 1 candidate is selected
    if (selectedCount === 1) {
      actions.push({
        label: "✏️ Edit",
        onClick: handleEditSelected,
      });
    }

    // Show Delete button when 1 or more candidates are selected
    if (selectedCount > 0) {
      actions.push({
        label: `🗑️ Delete (${selectedCount})`,
        onClick: handleDeleteSelected,
      });
    }
    
    return actions;
  };

  return (
    <>
      <div className="p-4">
        <Table
          title="Candidate Management"
          columns={columns}
          data={CandidatesPaginateData}
          actions={generateActions()}
          onRowClick={handleRowClick}
          selectedIds={selectedCandidateIds} // Pass selected IDs for highlighting
          loading={loading}
          currentPage={currentPage}
          totalPages={Math.ceil(filterCandidates.length / entries)}
          onPageChange={handlePageChange}
          totalEntries={filterCandidates.length}
          onSearch={handleSearch}
          onEntriesChange={handleEntriesChange}
          entriesOptions={[5, 10, 25, 50, 100]}
          exportButton={true}
        />
      </div>

      {isModalOpen && (
        <AddCandidateModal
          onClose={() => setIsModalOpen(false)}
          getCandidates={getCandidates}
          candidateData={candidateToEdit}
        />
      )}

      {emailModalOpen && (
        <EmailModal
          candidateData={selectedCandidateForEmail}
          onSubmit={handleEmailSubmit}
          onClose={() => setEmailModalOpen(false)}
          loading={loading}
        />
      )}
    </>
  );
};

export default Candidates;

